// Floating AI-agent dock — mounted once at AppLayout level, visible on
// every page. Staff-gated for now. Owns its own conversation state
// (client + active session + turns). "Open in full page" passes the
// active session_id through to /agent-chat so the conversation
// continues without losing context.
//
// Intentionally a slim chat surface — no voice, no attachments, no
// session sidebar. Power features stay on the full-page route so the
// dock is fast to open and predictable to use.
import {
  Box,
  Button,
  CircularProgress,
  Chip,
  Drawer,
  Fab,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import SmartToyIcon from '@mui/icons-material/SmartToy';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SendIcon from '@mui/icons-material/Send';
import AddIcon from '@mui/icons-material/Add';
import AttachFileIcon from '@mui/icons-material/AttachFile';

import paths from '@src/paths';
import { fetchClients } from '@src/api/actions/clients';
import clientInboxApi from '@src/api/clientInbox';
import { useMessage } from '@src/context/common';

import ChatBubble from './ChatBubble';
import { QUICK_QUESTIONS, QUICK_REQUESTS } from './quickActions';


// Width of the drawer paper across breakpoints. Full-width on phones so
// the input row + chat bubbles aren't cramped; capped at 520px on
// desktop so the main page is still legible behind the dock.
const DRAWER_WIDTH = { xs: '100%', sm: 480, md: 520 };

// Hide the FAB on these routes (the chat is already the page).
const HIDE_ON_PATHS = new Set([paths.agentChat]);

// Attachment limits mirror the full-page chat exactly so the agent
// pipeline accepts the same shapes from either surface.
const ATTACHMENT_LIMITS = {
  maxCount: 3,
  maxImageBytes: 5 * 1024 * 1024,
  maxTextBytes: 50 * 1024,
  imageTypes: ['image/png', 'image/jpeg', 'image/gif', 'image/webp'],
  textTypes: ['text/csv', 'text/plain', 'application/csv', 'application/json'],
};

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result || '';
      const comma = String(result).indexOf(',');
      resolve(comma >= 0 ? String(result).slice(comma + 1) : String(result));
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsText(file);
  });
}


export default function AgentChatDock() {
  const location = useLocation();
  const navigate = useNavigate();
  const message = useMessage();

  const [open, setOpen] = useState(false);

  // Conversation state — persists across drawer open/close because the
  // dock itself is mounted once at the layout level.
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [turns, setTurns] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);

  // Pending attachments — same shape as the full-page chat:
  //   { name, content_type, data_base64 | data_text, size }
  const [pendingAttachments, setPendingAttachments] = useState([]);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  // Auto-hide on the full-page route. (We still keep the component
  // mounted so its state survives a quick navigate-away-and-back.)
  const isHiddenRoute = HIDE_ON_PATHS.has(location.pathname);

  const clientsById = useMemo(() => {
    const map = {};
    clients.forEach((c) => { map[c.id] = c; });
    return map;
  }, [clients]);
  const selectedClientName = clientsById[selectedClientId]?.name || '';

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Load clients once when the dock first opens. Cached afterwards.
  useEffect(() => {
    if (!open || clients.length > 0) return;
    let active = true;
    fetchClients()
      .then((response) => {
        if (!active) return;
        const data = response.data?.results || response.data || [];
        const normalized = (Array.isArray(data) ? data : []).map((c) => ({
          id: String(c.id),
          name: c.name || 'Unnamed client',
          slug: c.slug || '',
        }));
        normalized.sort((a, b) => a.name.localeCompare(b.name));
        setClients(normalized);
      })
      .catch(() => {
        if (active) message.error('Unable to load clients for the AI dock.');
      });
    return () => { active = false; };
  }, [open, clients.length, message]);

  const handleNewChat = useCallback(() => {
    setActiveSessionId(null);
    setTurns([]);
    setInput('');
    setPendingAttachments([]);
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const handleFilesPicked = useCallback(async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    const available = ATTACHMENT_LIMITS.maxCount - pendingAttachments.length;
    if (available <= 0) {
      message.warning(`Max ${ATTACHMENT_LIMITS.maxCount} attachments per message.`);
      return;
    }
    const accepted = [];
    for (const file of files.slice(0, available)) {
      const ctype = (file.type || '').toLowerCase();
      try {
        if (ATTACHMENT_LIMITS.imageTypes.includes(ctype)) {
          if (file.size > ATTACHMENT_LIMITS.maxImageBytes) {
            message.warning(`${file.name} is too large (5MB max for images).`);
            continue;
          }
          const data = await readFileAsBase64(file);
          accepted.push({ name: file.name, content_type: ctype, data_base64: data, size: file.size });
        } else if (
          ATTACHMENT_LIMITS.textTypes.includes(ctype)
          || file.name.toLowerCase().endsWith('.csv')
          || file.name.toLowerCase().endsWith('.txt')
        ) {
          if (file.size > ATTACHMENT_LIMITS.maxTextBytes) {
            message.warning(`${file.name} is too large (50KB max for CSV/text).`);
            continue;
          }
          const text = await readFileAsText(file);
          accepted.push({ name: file.name, content_type: ctype || 'text/csv', data_text: text, size: file.size });
        } else {
          message.warning(`${file.name}: unsupported type (only images and CSV/text allowed).`);
        }
      } catch (err) {
        message.error(`Couldn't read ${file.name}.`);
      }
    }
    if (accepted.length) {
      setPendingAttachments((prev) => [...prev, ...accepted]);
    }
  }, [message, pendingAttachments.length]);

  const removeAttachment = useCallback((idx) => {
    setPendingAttachments((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const handleSend = useCallback((override) => {
    const raw = typeof override === 'string' ? override : input;
    const trimmed = (raw || '').trim();
    // Allow send if there's text OR at least one attachment (so the
    // agent can answer "what's in this screenshot?" with no prose).
    if ((!trimmed && !pendingAttachments.length) || !selectedClientId || sending) return;

    const displayContent = trimmed
      || (pendingAttachments.length === 1
        ? `(attached ${pendingAttachments[0].name})`
        : `(attached ${pendingAttachments.length} files)`);
    const tempUserTurn = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: displayContent,
      created_at: new Date().toISOString(),
    };
    setTurns((prev) => [...prev, tempUserTurn]);
    setInput('');
    setSending(true);
    setTimeout(scrollToBottom, 50);

    const streamingId = `assistant-${Date.now()}`;
    setTurns((prev) => [...prev, {
      id: streamingId,
      role: 'assistant',
      content: '',
      tool_calls: [],
      streaming: true,
      active_tool: null,
      created_at: new Date().toISOString(),
    }]);

    let gotAnyEvent = false;
    let finalized = false;

    const attachmentsToSend = pendingAttachments.map((a) => {
      const out = { name: a.name, content_type: a.content_type };
      if (a.data_base64) out.data_base64 = a.data_base64;
      if (a.data_text) out.data_text = a.data_text;
      return out;
    });
    if (pendingAttachments.length) setPendingAttachments([]);

    // Capture the page the user is asking from. Backend folds this
     // into the system prompt so "how do I do X on this page" works
     // without the user having to spell out which page they're on.
    const pageContext = {
      pathname: location.pathname || '',
      search: location.search || '',
    };

    clientInboxApi.streamChatMessage(
      {
        client: selectedClientId,
        message: trimmed,
        session: activeSessionId || undefined,
        attachments: attachmentsToSend.length ? attachmentsToSend : undefined,
        page_context: pageContext,
      },
      (event) => {
        gotAnyEvent = true;
        if (!event || typeof event !== 'object') return;
        if (event.type === 'session') {
          if (event.session_id && !activeSessionId) {
            setActiveSessionId(event.session_id);
          }
          return;
        }
        if (event.type === 'tool_start') {
          setTurns((prev) => prev.map((t) =>
            t.id === streamingId ? { ...t, active_tool: event.name } : t,
          ));
          return;
        }
        if (event.type === 'text_delta') {
          setTurns((prev) => prev.map((t) =>
            t.id === streamingId ? { ...t, content: (t.content || '') + (event.text || '') } : t,
          ));
          setTimeout(scrollToBottom, 30);
          return;
        }
        if (event.type === 'done') {
          finalized = true;
          setTurns((prev) => prev.map((t) =>
            t.id === streamingId ? { ...t, streaming: false, active_tool: null } : t,
          ));
          setSending(false);
          setTimeout(scrollToBottom, 50);
          inputRef.current?.focus();
        }
        if (event.type === 'error') {
          finalized = true;
          message.error(event.message || 'Chat error');
          setTurns((prev) => prev.filter((t) => t.id !== streamingId));
          setSending(false);
        }
      },
    ).then(() => {
      // Safety net for stream that ended without a `done` event.
      if (!finalized) {
        if (!gotAnyEvent) {
          message.error('Unable to get a response. Please try again.');
          setTurns((prev) => prev.filter((t) => t.id !== streamingId));
        } else {
          setTurns((prev) => prev.map((t) =>
            t.id === streamingId ? { ...t, streaming: false, active_tool: null } : t,
          ));
        }
        setSending(false);
        inputRef.current?.focus();
      }
    }).catch(() => {
      message.error('Unable to get a response. Please try again.');
      setTurns((prev) => prev.filter((t) => t.id !== streamingId));
      setSending(false);
    });
  }, [activeSessionId, input, location.pathname, location.search, message, pendingAttachments, scrollToBottom, selectedClientId, sending]);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  // "Open in full page" — preserves the active session by passing it
  // through the URL so AgentChatPage picks up where the dock left off.
  const handleOpenFullPage = useCallback(() => {
    const params = new URLSearchParams();
    if (selectedClientId) params.set('client', selectedClientId);
    if (activeSessionId) params.set('session', String(activeSessionId));
    const query = params.toString();
    navigate(query ? `${paths.agentChat}?${query}` : paths.agentChat);
    setOpen(false);
  }, [activeSessionId, navigate, selectedClientId]);

  if (isHiddenRoute) {
    return null;
  }

  return (
    <>
      {!open && (
        <Tooltip title="Ask the AI agent" placement="left">
          <Fab
            color="primary"
            onClick={() => setOpen(true)}
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: (theme) => theme.zIndex.speedDial,
              boxShadow: 6,
            }}
            aria-label="Open AI agent"
          >
            <SmartToyIcon />
          </Fab>
        </Tooltip>
      )}

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: DRAWER_WIDTH,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* Drawer header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <SmartToyIcon color="primary" />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Lead Gurus Agent
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Open in full page">
              <IconButton size="small" onClick={handleOpenFullPage}>
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Close">
              <IconButton size="small" onClick={() => setOpen(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {/* Client picker + new chat */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}
        >
          <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
            <InputLabel>Client</InputLabel>
            <Select
              label="Client"
              value={selectedClientId}
              onChange={(e) => {
                setSelectedClientId(e.target.value);
                setActiveSessionId(null);
                setTurns([]);
              }}
            >
              {clients.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Start a new chat">
            <span>
              <IconButton
                size="small"
                onClick={handleNewChat}
                disabled={!selectedClientId || sending}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>

        {/* Messages */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            px: 2,
            py: 2,
            bgcolor: 'grey.50',
            minHeight: 0,
          }}
        >
          {!selectedClientId ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Pick a client above to start chatting.
              </Typography>
            </Box>
          ) : turns.length === 0 ? (
            <Stack spacing={1.5} alignItems="center" sx={{ pt: 4 }}>
              <SmartToyIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Ask anything about {selectedClientName || 'this client'}.
              </Typography>
              <Stack spacing={1} sx={{ width: '100%', mt: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.4 }}>
                  ASK A QUESTION
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {QUICK_QUESTIONS.map((item) => (
                    <Chip
                      key={item.label}
                      label={item.label}
                      variant="outlined"
                      size="small"
                      disabled={sending}
                      onClick={() => handleSend(item.prompt)}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.4, mt: 1 }}>
                  SUBMIT A CHANGE REQUEST
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {QUICK_REQUESTS.map((item) => (
                    <Chip
                      key={item.label}
                      label={item.label}
                      color="primary"
                      variant="outlined"
                      size="small"
                      disabled={sending}
                      onClick={() => handleSend(item.prompt)}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Stack>
              </Stack>
            </Stack>
          ) : (
            <Stack spacing={2}>
              {turns.map((turn) => (
                <ChatBubble key={turn.id} turn={turn} isMobile />
              ))}
              {sending ? (
                <Box display="flex" alignItems="center" gap={1} pl={6}>
                  <CircularProgress size={14} />
                  <Typography variant="caption" color="text.secondary">
                    Thinking…
                  </Typography>
                </Box>
              ) : null}
              <div ref={messagesEndRef} />
            </Stack>
          )}
        </Box>

        {/* Input */}
        {selectedClientId ? (
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderTop: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              flexShrink: 0,
            }}
          >
            <TextField
              inputRef={inputRef}
              fullWidth
              multiline
              maxRows={4}
              size="small"
              placeholder={`Ask about ${selectedClientName || 'this client'}…`}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={sending}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Attach an image or CSV (max 3 files)">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={sending || pendingAttachments.length >= ATTACHMENT_LIMITS.maxCount}
                        >
                          <AttachFileIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <IconButton
                      color="primary"
                      onClick={() => handleSend()}
                      disabled={(!input.trim() && !pendingAttachments.length) || sending}
                      size="small"
                    >
                      <SendIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp,.csv,.txt,text/csv,text/plain"
              multiple
              onChange={handleFilesPicked}
              style={{ display: 'none' }}
            />
            {pendingAttachments.length ? (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                {pendingAttachments.map((att, i) => {
                  const sizeKb = att.size ? `${Math.round(att.size / 1024)}KB` : '';
                  return (
                    <Chip
                      key={`${att.name}-${i}`}
                      label={`${att.name}${sizeKb ? ` (${sizeKb})` : ''}`}
                      size="small"
                      onDelete={() => removeAttachment(i)}
                      deleteIcon={<CloseIcon />}
                    />
                  );
                })}
              </Stack>
            ) : null}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.75, textAlign: 'right' }}
            >
              <Button
                size="small"
                onClick={handleOpenFullPage}
                sx={{ textTransform: 'none', minWidth: 0, p: 0, fontSize: 'inherit' }}
              >
                Need voice input? Open full page ↗
              </Button>
            </Typography>
          </Box>
        ) : null}
      </Drawer>
    </>
  );
}
